import React from 'react';
import {
  Box,
  Paper,
  IconButton,
  Collapse,
  Typography
} from '@mui/material';
import { ExpandMore, ExpandLess, DragIndicator } from '@mui/icons-material';
import Draggable from 'react-draggable';
import { Trans } from '@lingui/react';
import { CustomUnitFields, UnitSystem } from 'providers/api';
import { RiseRateSeries } from 'modules/project/StopeFillPage/Three/riseRateSeries';
import RiseRateChart from './RiseRateChart';

interface RiseRatePanelProps {
  series: RiseRateSeries;
  pourNames: Map<string, string>;
  drawerOpen?: boolean;
  unitSystemPreference?: UnitSystem;
  customUnitFields?: CustomUnitFields[];
}

const RiseRatePanel = ({
  series, pourNames, drawerOpen = false, unitSystemPreference, customUnitFields,
}: RiseRatePanelProps) => {
  const [expanded, setExpanded] = React.useState(true);
  const nodeRef = React.useRef<HTMLDivElement>(null);

  // Mirrors StopeVolumePanel's sidebar allowance, dropped below it so the two do not open on top of
  // each other. Both are draggable, so this is only the starting position.
  const defaultX = drawerOpen ? -420 : -10;

  return (
    <Draggable
      nodeRef={nodeRef}
      defaultPosition={{ x: defaultX, y: 320 }}
      handle=".rise-rate-panel-handle"
    >
      <Paper
        ref={nodeRef}
        elevation={3}
        sx={{
          position: 'absolute',
          // The stage reserves the ribbon toolbar with padding-top, but an absolutely positioned
          // child measures `top` from the padding box, so that padding does not move it. Without
          // this the header bar - drag handle, title, collapse arrow - sits behind the ribbon.
          top: 'var(--stopes-toolbar-height, 0px)',
          right: 0,
          width: 380,
          bgcolor: 'background.paper',
          opacity: 0.95,
          zIndex: 1000,
          overflow: 'hidden',
        }}
      >
        <Box
          className="rise-rate-panel-handle"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1,
            py: 0.5,
            cursor: 'move',
            bgcolor: 'action.hover',
            borderBottom: expanded ? 1 : 0,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <DragIndicator fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              <Trans id="Rise Rate">Rise Rate</Trans>
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <RiseRateChart
            series={series}
            pourNames={pourNames}
            unitSystemPreference={unitSystemPreference}
            customUnitFields={customUnitFields}
          />
        </Collapse>
      </Paper>
    </Draggable>
  );
};

export default RiseRatePanel;
